import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LanguageSelector from './LanguageSelector';
import { getLocalizedValue, siteContent } from '../../data/siteContent';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/Header.css';

const sectionIds = ['home', 'schedule', 'coaches', 'contact'];
const swipeCloseThreshold = 64;
const swipeDirectionRatio = 1.25;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const menuToggleRef = useRef(null);
  const swipeStartRef = useRef(null);
  const location = useLocation();
  const { language } = useLanguage();
  const navigation = siteContent.navigation;

  useEffect(() => {
    if (location.pathname !== '/') {
      return undefined;
    }

    const updateActiveSection = () => {
      const headerHeight = document.querySelector('.header')?.offsetHeight || 80;
      const activationPoint = window.scrollY + headerHeight + 80;

      const currentSection = sectionIds.reduce((current, sectionId) => {
        const section = document.getElementById(sectionId);
        if (!section) return current;

        return section.offsetTop <= activationPoint ? sectionId : current;
      }, 'home');

      setActiveSection(currentSection);
    };

    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    window.addEventListener('resize', updateActiveSection);

    return () => {
      window.removeEventListener('scroll', updateActiveSection);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [location.pathname]);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    swipeStartRef.current = null;
  }, []);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      closeMenu();
      menuToggleRef.current?.focus();
    };

    document.body.classList.add('mobile-menu-open');
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.classList.remove('mobile-menu-open');
      document.body.style.overflow = previousBodyOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeMenu, isMenuOpen]);

  useEffect(() => {
    closeMenu();
  }, [closeMenu, location.hash, location.pathname]);

  useEffect(() => {
    const desktopMediaQuery = window.matchMedia('(min-width: 769px)');
    const handleViewportChange = (event) => {
      if (event.matches) {
        closeMenu();
      }
    };

    desktopMediaQuery.addEventListener('change', handleViewportChange);

    return () => {
      desktopMediaQuery.removeEventListener('change', handleViewportChange);
    };
  }, [closeMenu]);

  const toggleMenu = () => {
    setIsMenuOpen((wasMenuOpen) => !wasMenuOpen);
  };

  const isPageActive = (path) => (location.pathname === path ? 'active' : '');

  const isSectionActive = (sectionId) => {
    if (location.pathname !== '/') return '';

    return activeSection === sectionId ? 'active' : '';
  };

  const handleMenuPointerDown = (event) => {
    if (!isMenuOpen || event.pointerType === 'mouse') {
      swipeStartRef.current = null;
      return;
    }

    swipeStartRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      currentX: event.clientX,
      currentY: event.clientY,
    };
  };

  const handleMenuPointerMove = (event) => {
    if (swipeStartRef.current?.pointerId !== event.pointerId) {
      return;
    }

    swipeStartRef.current.currentX = event.clientX;
    swipeStartRef.current.currentY = event.clientY;
  };

  const handleMenuPointerEnd = (event) => {
    const swipe = swipeStartRef.current;
    swipeStartRef.current = null;

    if (!swipe || swipe.pointerId !== event.pointerId) {
      return;
    }

    const horizontalDistance = swipe.currentX - swipe.startX;
    const verticalDistance = Math.abs(swipe.currentY - swipe.startY);

    if (
      horizontalDistance >= swipeCloseThreshold
      && horizontalDistance >= verticalDistance * swipeDirectionRatio
    ) {
      closeMenu();
    }
  };

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    closeMenu();

    if (location.pathname === '/') {
      window.setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ block: 'start' });
      }, 0);
    }
  };

  const logoMarkup = (
    <>
      <div className="logo-icon">
        <img src="/images/logo.svg" alt="Team Agua Logo" className="logo-image" width="765" height="255" />
        <img src="/images/logo-teal.svg" alt="" className="logo-image logo-image-hover" width="765" height="255" aria-hidden="true" />
      </div>
      <span className="logo-text">{siteContent.organization.name}</span>
    </>
  );

  return (
    <>
    <header className="header">
      <div className="header-container">
        <Link to="/" className="logo" onClick={closeMenu}>
          {logoMarkup}
        </Link>

        <button
          ref={menuToggleRef}
          type="button"
          className={`menu-toggle ${isMenuOpen ? 'menu-toggle-open' : ''}`}
          onClick={toggleMenu}
          aria-controls="primary-navigation"
          aria-expanded={isMenuOpen}
          aria-label={getLocalizedValue(isMenuOpen ? navigation.closeMenu : navigation.openMenu, language)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div className="header-right">
          <button
            type="button"
            className={`mobile-menu-backdrop ${isMenuOpen ? 'mobile-menu-backdrop-open' : ''}`}
            onClick={closeMenu}
            aria-label={getLocalizedValue(navigation.returnToPage, language)}
            tabIndex={isMenuOpen ? 0 : -1}
          />
          <nav
            id="primary-navigation"
            className={`nav ${isMenuOpen ? 'nav-open' : ''}`}
            onPointerDown={handleMenuPointerDown}
            onPointerMove={handleMenuPointerMove}
            onPointerUp={handleMenuPointerEnd}
            onPointerCancel={() => { swipeStartRef.current = null; }}
          >
            <Link to="/#home" className={`nav-link ${isSectionActive('home')}`} onClick={() => handleSectionClick('home')}>
              {getLocalizedValue(navigation.home, language)}
            </Link>
            <Link to="/#schedule" className={`nav-link ${isSectionActive('schedule')}`} onClick={() => handleSectionClick('schedule')}>
              {getLocalizedValue(navigation.schedule, language)}
            </Link>
            <Link to="/#coaches" className={`nav-link ${isSectionActive('coaches')}`} onClick={() => handleSectionClick('coaches')}>
              {getLocalizedValue(navigation.coaches, language)}
            </Link>
            <Link to="/#contact" className={`nav-link ${isSectionActive('contact')}`} onClick={() => handleSectionClick('contact')}>
              {getLocalizedValue(navigation.contact, language)}
            </Link>
            <div className="nav-language">
              <LanguageSelector />
            </div>
            <Link
              to="/signup"
              className={`nav-link nav-link-cta agua-crooked-frame ${location.pathname === '/' && activeSection === 'home' ? 'nav-link-cta-hidden' : ''} ${isPageActive('/signup')}`}
              onClick={closeMenu}
            >
              {getLocalizedValue(navigation.signup, language)}
            </Link>
          </nav>
        </div>
      </div>
    </header>
    <Link to="/" className="logo logo-hover-layer" aria-hidden="true" tabIndex="-1">
      {logoMarkup}
    </Link>
    </>
  );
};

export default Header;
